import { Text } from 'slate';

import { maxValueBy } from '../../util';
import type {
  AlineText,
  ListMarker,
  PageDimensions,
  ParagraphElement,
  RenderModelLine,
  RenderModelLineChild,
  RenderModelParagraph,
} from '../internal';
import { isDrawingElement, isSignatureFieldElement, Queries } from '../internal';

// 1.1875 derived from Google Docs default line height.
export const LINE_HEIGHT_FACTOR = 1.1875;

export function createPageDimensions(pageDimensions?: Partial<PageDimensions>): PageDimensions {
  return {
    height: 11,
    marginBottom: 1,
    marginLeft: 1,
    marginRight: 1,
    marginTop: 1,
    width: 8.5,
    ...pageDimensions,
  };
}

export function pageContentWidth(pageDimensions: PageDimensions): number {
  return pageDimensions.width - pageDimensions.marginLeft - pageDimensions.marginRight;
}

export function calculateLineHeightPx(fontSizePx: number, lineSpacing: number): number {
  return fontSizePx * lineSpacing * LINE_HEIGHT_FACTOR;
}

/**
 * Determines line height in pixels for all Aline line nodes.
 */
export function getLineHeightPx(
  lineSpacing: number,
  { children, maxDescentPx }: Pick<RenderModelLine, 'children' | 'maxDescentPx'>,
  listMarker?: ListMarker | null
): number {
  const lineChildren: (RenderModelLineChild | AlineText)[] = [...children];
  if (listMarker) lineChildren.push({ ...listMarker, comments: [], suggestedEdits: [], text: '' });

  return maxValueBy(
    lineChildren,
    child => getLineChildHeightPx(child, { lineSpacing, maxDescentPx }),
    0
  );
}

/**
 * Calculates the height of a line child node.
 */
function getLineChildHeightPx(
  child: RenderModelLineChild | AlineText,
  options: { lineSpacing: number; maxDescentPx: number }
): number {
  const { lineSpacing, maxDescentPx } = options;

  if (Text.isText(child)) {
    const { text, ...textFormats } = child;
    const fontSizePx = Queries.activeFontSizePx(textFormats);
    return calculateLineHeightPx(fontSizePx, lineSpacing);
  }

  // Descendants of SignatureField elements don't contribute to height.  We have a hard-coded fixed height for the field.
  if (isSignatureFieldElement(child))
    return maxDescentPx + Queries.getSignatureImageDimensions(child).imgHeightPx;

  if (isDrawingElement(child))
    return maxDescentPx + Queries.getDrawingDimensions(child).containerHeightPx;

  return maxValueBy(child.children, nestedChild => getLineChildHeightPx(nestedChild, options), 0);
}

/**
 * Use this convenience type as sometimes we need to call this function to determine the height of a render model paragraph
 * and we don't have the full render model paragraph type completed yet.  See src/layout-engine/render-model/render-model-paragraph.ts for example.
 */
interface RenderModelParaSubset extends Omit<ParagraphElement, 'children'> {
  children: RenderModelParagraph['children'];
  prevParaSpacingAfterPx: number | null;
  spacingAfterSuppressed: boolean;
  spacingBeforeSuppressed: boolean;
  splitIndex: number;
}

export function getParagraphHeightPx(
  paragraph: RenderModelParaSubset,
  borderInsets: { bottomPx: number; topPx: number }
): number {
  const spacingAfterPx = Queries.renderedParagraphSpacingAfter(paragraph, 'px');
  const spacingBeforePx = Queries.renderedSpacingBeforePx(paragraph);

  /**
   * Note: Do not extract out the adjustment for spacingBefore and spacingAfter out of the loop because, sometimes, when building pages we pass a paragraph into
   * this function that may not include the last line or the first line (e.g., if we are splitting the paragraph).  So, we only want to take into account spacingBefore
   * and spacingAfter if the paragraph we pass in actually has the first line or the last line.
   */
  let heightPx = 0;
  for (const line of paragraph.children) {
    heightPx += Queries.adjustedLineHeight(line, spacingAfterPx, spacingBeforePx);
  }

  return heightPx + borderInsets.topPx + borderInsets.bottomPx;
}
